'use client';
import * as React from 'react';
import { PropsWithChildren, useEffect, useState, type FC } from 'react';
import { XIcon, PlusIcon, FileText } from 'lucide-react';
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useAuiState,
  useAui,
} from '@assistant-ui/react';
import { useShallow } from 'zustand/shallow';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogTrigger,
} from '../ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { TooltipIconButton } from './tooltip-icon-button';

const useFileSrc = (file: File | undefined) => {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return src;
};

const useAttachmentSrc = () => {
  const { file, src } = useAuiState(
    useShallow(({ attachment }): { file?: File; src?: string } => {
      if (attachment.type !== 'image') {return {}; }
      if (attachment.file) {return { file: attachment.file }; }
      const src = attachment.content?.filter((c) => c.type === 'image')[0]
        ?.image;
      if (!src) {return {}; }
      return { src };
    }),
  );

  return useFileSrc(file) ?? src;
};

type AttachmentPreviewProps = {
  src: string;
};

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
  // const [isLoaded, setIsLoaded] = useState(false);
  return (
    <img
      src={src}
      alt="Image Preview"
      width={1}
      height={1}
      className={'aui-attachment-preview-image-loaded'}
      // onLoadingComplete={() => setIsLoaded(true)}
      // priority={false}
    />
  );
};

const AttachmentPreviewDialog: FC<PropsWithChildren> = ({ children }) => {
  const src = useAttachmentSrc();

  if (!src) {return children; }

  return (
    <Dialog>
      <DialogTrigger className="aui-attachment-preview-trigger" asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="aui-attachment-preview-dialog-content">
        <DialogTitle className="aui-sr-only">
          Image Attachment Preview
        </DialogTitle>
        <div className="aui-attachment-preview">
          <AttachmentPreview src={src} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AttachmentThumb: FC = () => {
  const isImage = useAuiState(
    ({ attachment }) => attachment.type === 'image',
  );
  const src = useAttachmentSrc();

  return (
    <Avatar className="aui-attachment-tile-avatar">
      <AvatarImage
        src={src}
        alt="Attachment preview"
        className="aui-attachment-tile-image"
      />
      <AvatarFallback delayMs={isImage ? 200 : 0}>
        <FileText className="aui-attachment-tile-fallback-icon" />
      </AvatarFallback>
    </Avatar>
  );
};

const AttachmentUI: FC = () => {
  const api = useAui();
  const isComposer = api.attachment.source === 'composer';

  const isImage = useAuiState(
    ({ attachment }) => attachment.type === 'image',
  );
  const typeLabel = useAuiState(({ attachment }) => {
    type FT = 'file' | 'image' | 'document' | null;
    const fileType: FT = attachment.type;
    switch (fileType) {
      case 'image':
        return 'Image';
      case 'document':
        return 'Document';
      case 'file':
        return 'File';
        // throw new Error(`Unknown attachment type: ${type}`);
    }
    return null;
  });

  return (
    <Tooltip>
      <AttachmentPrimitive.Root
        className={cn(
          'aui-attachment-root',
          isImage && 'aui-attachment-root-composer',
        )}
      >
        <AttachmentPreviewDialog>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'aui-attachment-tile',
                isComposer && 'aui-attachment-tile-composer',
              )}
              role="button"
              id="attachment-tile"
              aria-label={`${typeLabel} attachment`}
            >
              <AttachmentThumb />
            </div>
          </TooltipTrigger>
        </AttachmentPreviewDialog>
        {isComposer && <AttachmentRemove />}
      </AttachmentPrimitive.Root>
      <TooltipContent side="top">
        <AttachmentPrimitive.Name />
      </TooltipContent>
    </Tooltip>
  );
};

const AttachmentRemove: FC = () => {
  return (
    <AttachmentPrimitive.Remove asChild>
      <TooltipIconButton
        tooltip="Remove file"
        className="aui-attachment-tile-remove"
        side="top"
      >
        <XIcon className="aui-attachment-remove-icon" />
      </TooltipIconButton>
    </AttachmentPrimitive.Remove>
  );
};

export const UserMessageAttachments: FC = () => {
  return (
    <div className="aui-user-message-attachments-end">
      <MessagePrimitive.Attachments components={{ Attachment: AttachmentUI }} />
    </div>
  );
};

export const ComposerAttachments: FC = () => {
  return (
    <div className="aui-composer-attachments">
      <ComposerPrimitive.Attachments
        components={{ Attachment: AttachmentUI }}
      />
    </div>
  );
};

export const ComposerAddAttachment: FC = () => {
  return (
    <ComposerPrimitive.AddAttachment asChild>
      <TooltipIconButton
        tooltip="Add Attachment"
        side="bottom"
        variant="ghost"
        size="icon"
        className="aui-composer-add-attachment"
        aria-label="Add Attachment"
      >
        <PlusIcon className="aui-attachment-add-icon" />
      </TooltipIconButton>
    </ComposerPrimitive.AddAttachment>
  );
};
